import { Suspense, lazy, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveLanguage } from '@dorixona/i18n'
import { Press, cx } from '@dorixona/ui'
import { useApi } from '../../../api/context'
import { storiesBackend, usePublishedStories } from '../api'
import { markSeen, pick, readSeen, writeSeen, type Story } from '../model'

// Просмотр со Swiper грузится, только когда сторис открыли: главная его не ждёт.
const StoryViewer = lazy(() => import('./StoryViewer'))

const SKELETON = [0, 1, 2, 3, 4]

/**
 * Сторис на главной: ряд кружков в серверном порядке. Кольцо у
 * непросмотренных — градиент бренда, у просмотренных гаснет; изменённая
 * сторис снова становится непросмотренной. Полка — промо, а не содержимое
 * главной: при ошибке или пустой витрине её просто нет.
 */
export function StoriesShelf() {
  const { t, i18n } = useTranslation(['home', 'common'])
  const language = resolveLanguage([i18n.language])
  const api = useApi()
  // Просмотр уходит на сервер один раз за показ полки: отметка «увидел» может
  // прийти дважды раньше, чем обновится состояние.
  const viewed = useRef(new Set<number>())
  const stories = usePublishedStories()
  const [seen, setSeen] = useState<string[]>(() => readSeen())
  const [opened, setOpened] = useState<number | null>(null)

  if (stories.isPending) {
    return (
      <div className="sect" role="status" aria-busy="true">
        <span className="visually-hidden">{t('common:state.loading')}</span>
        <ul className="stories" aria-hidden="true">
          {SKELETON.map((i) => (
            <li key={i} className="story">
              <i className="sk-b story__sk-ring" />
              <i className="sk-b sk-l story__sk-cap" />
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const list = stories.data ?? []
  if (list.length === 0) return null

  const onSeen = (story: Story) => {
    const next = markSeen(seen, story, list)
    if (next === seen) return
    setSeen(next)
    writeSeen(next)
    // Счётчик просмотров в админке. Сервер считает покупателя один раз, гостя
    // не считает; сбой отметки покупателю не показываем — это статистика.
    if (viewed.current.has(story.id)) return
    viewed.current.add(story.id)
    storiesBackend(api)
      .view(story.id)
      .catch(() => undefined)
  }

  return (
    <div className="sect">
      <h2 className="visually-hidden">{t('home:stories.title')}</h2>
      <ul className="stories">
        {list.map((story, index) => {
          const viewed = seen.includes(story.seenKey)
          const caption = pick(story.title, language)
          return (
            <li key={story.id}>
              <Press
                squish="card"
                className={cx('story', viewed && 'is-viewed')}
                // Кольцо — не единственный признак новой сторис: диктор слышит
                // «новая» (color.md › Inclusive color). Имя начинается с подписи —
                // голосовое управление находит кнопку по видимому тексту.
                aria-label={viewed ? undefined : `${caption}, ${t('home:stories.unseen')}`}
                onPress={() => {
                  setOpened(index)
                }}
              >
                <span className="story__ring">
                  <img className="story__art story__img" src={story.cover} alt="" />
                </span>
                <span className="story__cap">{caption}</span>
              </Press>
            </li>
          )
        })}
      </ul>

      {opened === null ? null : (
        <Suspense fallback={null}>
          <StoryViewer
            stories={list}
            start={opened}
            language={language}
            onSeen={onSeen}
            onClose={() => {
              setOpened(null)
            }}
          />
        </Suspense>
      )}
    </div>
  )
}
